/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$grid-control-v-gutter-phone: 16px !default;
$grid-control-v-gutter-tablet: 24px !default;
$grid-control-v-gutter-desktop: 32px !default;
$grid-control-v-gutter-desktop-large: 48px !default;
$grid-control-h-gutter-phone: 16px !default;
$grid-control-h-gutter-tablet: 24px !default;
$grid-control-h-gutter-desktop: 32px !default;
$grid-control-h-gutter-desktop-large: 48px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

@function calcColumnWidth($elementColumnStretch, $breakpoint, $deactivateGutter: $removeVerticalGutter) {
  $gutterWidth: $grid-control-v-gutter-desktop-large;

  @if ($deactivateGutter) {
    $gutterWidth: 0px;
  } @else if ($breakpoint == "desktop") {
    $gutterWidth: $grid-control-v-gutter-desktop;
  } @else if ($breakpoint == "tablet") {
    $gutterWidth: $grid-control-v-gutter-tablet;
  } @else if ($breakpoint == "phone") {
    $gutterWidth: $grid-control-v-gutter-phone;
  }

  // Each elements width is defined by the number of columns over which it stretches.
  // To this we need to add a number of gaps equal to the number of columns it stretches - 1.
  $width: calc(((100% - $gutterWidth * 11) / 12) * $elementColumnStretch + $gutterWidth * ($elementColumnStretch - 1));

  @return $width;
}

/* Function generates width in percent per each column */
@mixin generate-column-width($n, $i: 1) {
  @if ($i <= $n) {
    @at-root &.#{$prefix}grid-column-#{$i} {
      grid-column: span $i;
    }
    @include generate-column-width($n, ($i + 1));
  }
}

:host {
  display: block;
}

* {
  margin: 0;
  padding: 0;
}

*,
*::after,
*::before {
  box-sizing: border-box;
  outline: 0 none;
  text-rendering: optimizelegibility;
}

.#{$prefix}grid-container {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto;
  justify-content: start;
  align-content: start;
  column-gap: $grid-control-v-gutter-desktop-large;
  row-gap: $grid-control-h-gutter-desktop-large;

  @include for-size(desktop) {
    column-gap: $grid-control-v-gutter-desktop;
    row-gap: $grid-control-h-gutter-desktop;
  }

  @include for-size(tablet) {
    column-gap: $grid-control-v-gutter-tablet;
    row-gap: $grid-control-h-gutter-tablet;
  }

  @include for-size(phone) {
    column-gap: $grid-control-v-gutter-phone;
    row-gap: $grid-control-h-gutter-phone;
  }

  &.#{$prefix}force-mobile-layout {
    column-gap: $grid-control-v-gutter-phone;
    row-gap: $grid-control-h-gutter-phone;
  }

  > .#{$prefix}grid-column {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    &.#{$prefix}top {
      justify-content: flex-start;
    }

    &.#{$prefix}center {
      justify-content: center;
    }

    &.#{$prefix}bottom {
      justify-content: flex-end;
    }
  }

  &.#{$prefix}same-height {
    > .#{$prefix}grid-column {
      display: flex;
      flex-direction: column;
    }
  }

  &.#{$prefix}remove-vertical-gutter {
    column-gap: 0;
  }

  &.#{$prefix}remove-horizontal-gutter {
    row-gap: 0;

    > .#{$prefix}grid-column {
      padding-bottom: 0;
    }
  }
}

/* :host(.ignore-column-gaps) {
  .#{$prefix}grid-container {
    column-gap: $grid-control-v-gutter-phone;
    row-gap: $grid-control-h-gutter-phone;

    &.#{$prefix}remove-vertical-gutter {
      column-gap: 0;
    }

    &.#{$prefix}remove-horizontal-gutter {
      row-gap: 0;

      > .#{$prefix}grid-column {
        padding-bottom: 0;
      }
    }
  }
} */

/*
█▀▀ █▀█ █░░ █░█ █▀▄▀█ █▄░█   █▀▄ █▀▀ █▀▀ █ █▄░█ █ ▀█▀ █ █▀█ █▄░█ █▀
█▄▄ █▄█ █▄▄ █▄█ █░▀░█ █░▀█   █▄▀ ██▄ █▀░ █ █░▀█ █ ░█░ █ █▄█ █░▀█ ▄█
*/

/*
 * grid__d9-3__t8-4
 */
@mixin grid__d9-3__t8-4($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 9, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 9, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d9-3__t8-4 {
  @include grid__d9-3__t8-4($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d9-3__t8-4($removeVerticalGutter: true);
  }
}

/*
 * grid__d3-9__t4-8
 */
@mixin grid__d3-9__t4-8($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 9, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 9, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d3-9__t4-8 {
  @include grid__d3-9__t4-8($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d3-9__t4-8($removeVerticalGutter: true);
  }
}

/*
 * grid__d6-3-3__t12-6-6
 */
@mixin grid__d6-3-3__t12-6-6($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);

    > .#{$prefix}grid-column {
      &.#{$prefix}grid-column-6 {
        grid-column: span 2;
      }

      &.#{$prefix}grid-column-3 {
        grid-column: span 1;
      }
    }
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}
.#{$prefix}grid__d6-3-3__t12-6-6 {
  @include grid__d6-3-3__t12-6-6($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d6-3-3__t12-6-6($removeVerticalGutter: true);
  }
}

/*
 * grid__d3-3-6__t6-6-12
 */
@mixin grid__d3-3-6__t6-6-12($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);

    > .#{$prefix}grid-column {
      &.#{$prefix}grid-column-6 {
        grid-column: span 2;
      }

      &.#{$prefix}grid-column-3 {
        grid-column: span 1;
      }
    }
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d3-3-6__t6-6-12 {
  @include grid__d3-3-6__t6-6-12($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d3-3-6__t6-6-12($removeVerticalGutter: true);
  }
}

/*
 * grid__d3-3-3-3__t6-6-6-6
 */
@mixin grid__d3-3-3-3__t6-6-6-6($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);

    > .#{$prefix}grid-column {
      &.#{$prefix}grid-column-6 {
        grid-column: span 2;
      }
    }
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d3-3-3-3__t6-6-6-6 {
  @include grid__d3-3-3-3__t6-6-6-6($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d3-3-3-3__t6-6-6-6($removeVerticalGutter: true);
  }
}

/*
 * grid__d3-3-3-3__t12
 */
@mixin grid__d3-3-3-3__t12($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 3, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns: 100%;
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}
.#{$prefix}grid__d3-3-3-3__t12 {
  @include grid__d3-3-3-3__t12($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d3-3-3-3__t12($removeVerticalGutter: true);
  }
}

/*
 * grid__d2-2-2-2-2-2__t12
 */
@mixin grid__d2-2-2-2-2-2__t12($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns: 100%;
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}
.#{$prefix}grid__d2-2-2-2-2-2__t12 {
  @include grid__d2-2-2-2-2-2__t12($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d2-2-2-2-2-2__t12($removeVerticalGutter: true);
  }
}

/*
 * grid__d2-8-2
 */
@mixin grid__d2-8-2($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 2, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 1, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 10, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 1, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);

    > .#{$prefix}grid-column {
      &.#{$prefix}grid-column-8 {
        grid-column: 2 / span 1;
        order: 3;
      }

      &.#{$prefix}grid-column-2 {
        grid-column: 2 / span 1;

        &:last-child {
          order: 2;
        }

        &:empty {
          display: none;
        }
      }
    }
  }
}
.#{$prefix}grid__d2-8-2 {
  @include grid__d2-8-2($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d2-8-2($removeVerticalGutter: true);
  }
}

/*
 * grid__d4-4-4__t4-4-4
 */
@mixin grid__d4-4-4__t4-4-4($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d4-4-4__t4-4-4 {
  @include grid__d4-4-4__t4-4-4($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d4-4-4__t4-4-4($removeVerticalGutter: true);
  }
}

/*
 * grid__d4-4-4__t12
 */
@mixin grid__d4-4-4__t12($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns: 100%;
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d4-4-4__t12 {
  @include grid__d4-4-4__t12($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d4-4-4__t12($removeVerticalGutter: true);
  }
}

/*
 * grid__d4-8__t4-8
 */
@mixin grid__d4-8__t4-8($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d4-8__t4-8 {
  @include grid__d4-8__t4-8($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d4-8__t4-8($removeVerticalGutter: true);
  }
}

/*
 * grid__d8-4__t8-4
 */
@mixin grid__d8-4__t8-4($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 8, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 4, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d8-4__t8-4 {
  @include grid__d8-4__t8-4($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d8-4__t8-4($removeVerticalGutter: true);
  }
}

/*
 * grid__d6-6__t6-6
 */
@mixin grid__d6-6__t6-6($removeVerticalGutter: false) {
  grid-template-columns:
    calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter)
    calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktopLarge, $deactivateGutter: $removeVerticalGutter);

  @include for-size(desktop) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: desktop, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(tablet) {
    grid-template-columns:
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter)
      calcColumnWidth($elementColumnStretch: 6, $breakpoint: tablet, $deactivateGutter: $removeVerticalGutter);
  }

  @include for-size(phone) {
    grid-template-columns: 100%;
  }

  &.#{$prefix}force-mobile-layout {
    grid-template-columns: 100%;
  }
}

.#{$prefix}grid__d6-6__t6-6 {
  @include grid__d6-6__t6-6($removeVerticalGutter: false);

  &.#{$prefix}remove-vertical-gutter {
    @include grid__d6-6__t6-6($removeVerticalGutter: true);
  }
}
